import {
  Box,
  FormControl,
  FormGroup,
  FormHelperText,
  FormLabel,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  TextField,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/system'
import React, { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetchEventOptions } from '../../hooks/events/useFetchEventOptions'
import { useMountEffect } from '../../hooks/useMountEffect'
import useValidationMessages from '../../hooks/useValidationMessages'
import Logo from '../../Images/Branding/cc-logo-white.svg'
import { Event, EventCategory } from '../../swagger'
import { EventFormValidFields, EventLocationTypes } from '../Card/EventFormCard'
import {
  EventFormCardVariants,
  EventFormValidationMessageTypes,
} from './EventEnums'
import { EventRegistrationRolesSelector } from './EventRegistrationRolesSelector'

export interface EventInformationDetails
  extends Pick<
    Event,
    'capacity' | 'owner' | 'possibleNewOwners' | 'publicUrl' | 'qRCodeImage'
  > {
  eventType: string
  category: string
  eventLocationType: string
  eventName: string
  leadershipTrainingRoles?: string[]
}

interface EventInformationFieldsProps {
  eventInformationDetails: EventInformationDetails
  eventFieldValidity: EventFormValidFields
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  isFieldDisabled: boolean
  onOwnerChange: (event: SelectChangeEvent<unknown>, child: ReactNode) => void
  variant: EventFormCardVariants
  onSelectedRolesChange: (selectedRoles: string[]) => void
  isEventTypeLocked?: boolean
}

const EventSection = styled('section')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

export const PublicUrlLink = styled('a')(({ theme }) => ({
  paddingLeft: '15px',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  marginLeft: theme.spacing(2),
  '&:hover': { textDecoration: 'underline' },
}))

const CapacityField = styled(TextField)(({ theme }) => ({
  width: '50% !important',
  [theme.breakpoints.down('sm')]: {
    width: '99% !important',
  },
  [theme.breakpoints.down('xs')]: {
    width: '98% !important',
  },
}))

const LocationTypeField = styled(TextField)(({ theme }) => ({
  width: '50% !important',
  [theme.breakpoints.down('sm')]: {
    width: '99% !important',
  },
  [theme.breakpoints.down('xs')]: {
    width: '98% !important',
  },
}))

const OwnerField = styled(TextField)(({ theme }) => ({
  width: '97% !important',
  [theme.breakpoints.down('sm')]: {
    width: '99% !important',
  },
  [theme.breakpoints.down('xs')]: {
    width: '98% !important',
  },
}))

export const EventInformationFields: React.FC<EventInformationFieldsProps> = (
  props
) => {
  const theme = useTheme()
  const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation()
  const {
    eventInformationDetails,
    eventFieldValidity,
    handleInputChange,
    isFieldDisabled,
    onOwnerChange,
    variant,
    onSelectedRolesChange,
    isEventTypeLocked = false,
  } = props
  const {
    capacity,
    eventType,
    eventLocationType,
    eventName,
    owner,
    possibleNewOwners,
    publicUrl,
    qRCodeImage,
    category,
  } = eventInformationDetails
  const isEditVariant = variant === EventFormCardVariants.EditEvent

  const { triggerFetch, eventTypes, leadershipTrainingRoles } =
    useFetchEventOptions({ includeRoles: true })

  useMountEffect(triggerFetch)

  const handleEventTypeChange = (
    changeEvent: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isEventTypeLocked) {
      return
    }

    const [selectedCategory, selectedEventType] =
      changeEvent.target.value.split('/')

    handleInputChange({
      target: {
        name: 'eventType',
        value: selectedEventType,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>)

    handleInputChange({
      target: {
        name: 'category',
        value: selectedCategory,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>)
  }

  const eventLocationTypeOptions = [
    t('Event.EventLocationTypeOptions.Local', 'Local'),
    t('Event.EventLocationTypeOptions.Global', 'Global'),
  ]

  const alphabeticallySortedOwners = useMemo(() => {
    return [...(possibleNewOwners ?? [])].sort((left, right) =>
      left.user.localeCompare(right.user)
    )
  }, [possibleNewOwners])

  const validationMessageMap = useValidationMessages([
    {
      field: EventFormValidationMessageTypes.EventName,
      message: t(
        'Events.EventInformationField.ValidationMessage.EventName',
        'Event Name cannot be empty.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventType,
      message: t(
        'Events.EventInformationField.ValidationMessage.EventType',
        'Please select an Event Type.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.Registration,
      message: t(
        'Events.EventInformationField.ValidationMessage.Registration',
        'Please select a Registration Status.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.MaxCapacity,
      message: t(
        'Events.EventInformationField.ValidationMessage.MaxCapacity',
        'Max Capacity must be between 1 and 999.'
      ),
    },
    {
      field: EventFormValidationMessageTypes.EventLocationType,
      message: t(
        'Events.EventInformationField.ValidationMessage.EventLocationType',
        'Please select an Event Location Type.'
      ),
    },
  ])

  const eventTypesOptions =
    Object.entries(eventTypes).length === 0
      ? {
          [category]: [{ id: eventType, name: eventType }],
        }
      : eventTypes

  const eventTypeHelperText = !eventFieldValidity.eventType.input
    ? validationMessageMap.get(EventFormValidationMessageTypes.EventType)
    : isEventTypeLocked
    ? t(
        'Events.EventInformationField.EventTypeLockedMessage',
        'You cannot change the Event Type because people are already registered.'
      )
    : null

  const resolvedEventTypeValue =
    category && eventType && !isFieldDisabled && !isEventTypeLocked
      ? `${category}/${eventType}`
      : eventType

  return (
    <EventSection aria-labelledby="eventDetailsHeader">
      <Box
        sx={{
          background: theme.palette.primary.main,
          ...(eventLocationType === EventLocationTypes.Global && {
            background: theme.palette.customBackground.logo,
          }),
          width: 290,
          height: 290,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'top',
          [theme.breakpoints.down('sm')]: {
            width: !isMobileOrSmaller ? '100%' : theme.spacing(13),
            height: theme.spacing(13),
          },
        }}
      >
        {qRCodeImage ? (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-link"
          >
            <img
              src={qRCodeImage}
              alt={t(
                'Events.EventInformationField.AltText',
                'QR Code with Logo'
              )}
              style={{
                width: !isMobileOrSmaller
                  ? theme.spacing(28)
                  : theme.spacing(12),
              }}
            />
          </a>
        ) : (
          <img
            src={Logo}
            alt={t('Events.EventInformationField.AltText', 'Event Image')}
            style={{
              ...(eventLocationType === EventLocationTypes.Global && {
                filter:
                  'brightness(0) saturate(100%) invert(9%) sepia(18%) saturate(6857%) hue-rotate(200deg) brightness(98%) contrast(99%)',
              }),
              width: !isMobileOrSmaller ? 160 : 70,
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '& .MuiTextField-root': {
            width: 525,
            margin: theme.spacing(1),
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiTextField-root': {
              width: `calc(100% - ${theme.spacing(1)})`,
            },
          },
        }}
      >
        <TextField
          label={t('Events.EventInformationField.FormField.name', 'Event Name')}
          inputProps={{
            'aria-label': t(
              'Events.EventInformationField.AriaLabel.name',
              'Event Name'
            ),
          }}
          name="eventName"
          variant="filled"
          value={eventName}
          onChange={handleInputChange}
          disabled={isFieldDisabled}
          error={!eventFieldValidity.eventName.input}
          helperText={
            !eventFieldValidity.eventName.input
              ? validationMessageMap.get(
                  EventFormValidationMessageTypes.EventName
                )
              : null
          }
        />

        <TextField
          label={t(
            'Events.EventInformationField.FormField.EventType',
            'Event Type'
          )}
          variant="filled"
          select={!isFieldDisabled && !isEventTypeLocked}
          value={resolvedEventTypeValue}
          name="eventType"
          onChange={handleEventTypeChange}
          disabled={isFieldDisabled || isEventTypeLocked}
          error={!eventFieldValidity.eventType.input}
          helperText={eventTypeHelperText}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  py: 0.5,
                  backgroundColor: '#FEFAF7',
                  '& .MuiMenuItem-root': {
                    fontSize: '0.95rem',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1.3,
                  },
                  '& .MuiListSubheader-root': {
                    fontWeight: 600,
                    fontSize: '0.94rem',
                    lineHeight: 2,
                    color: '#000',
                    textTransform: 'uppercase',
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="">
            {t('Events.EventInformationField.SelectPlaceholder', 'Select...')}
          </MenuItem>

          {Object.entries(eventTypesOptions).flatMap(([category, items]) => {
            return [
              <ListSubheader disableSticky key={`hdr-${category}`}>
                {category}
              </ListSubheader>,
              items.map(({ id, name }) => (
                <MenuItem key={id} value={`${category}/${name}`} sx={{ pl: 4 }}>
                  {name}
                </MenuItem>
              )),
            ]
          })}
        </TextField>

        {isEditVariant ? (
          <FormControl
            component="fieldset"
            sx={{
              margin: '8px',
              [theme.breakpoints.down('sm')]: {
                margin: '11px 0px 0px 7px',
              },
            }}
          >
            <FormLabel
              component="legend"
              sx={{
                color: '#8a929f',
                transform: 'translate(12px, -2px) scale(0.75)',
              }}
              aria-label={t(
                'Events.EventInformationField.AriaLabel.PublicUrl',
                'Public Url'
              )}
            >
              {t('Events.EventInformationField.FormField.PublicUrl', 'Link')}
            </FormLabel>
            <FormGroup>
              <PublicUrlLink id={publicUrl} href={publicUrl} target="_blank">
                {publicUrl}
              </PublicUrlLink>
            </FormGroup>
            <hr />
          </FormControl>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
              display: 'grid',
            },
          }}
        >
          <CapacityField
            label={t(
              'Events.EventInformationField.FormField.Capacity',
              'Capacity'
            )}
            inputProps={{
              'aria-label': t(
                'Events.EventInformationField.AriaLabel.Capacity',
                'Capacity'
              ),
            }}
            variant="filled"
            value={capacity?.toString() || ''}
            name="capacity"
            onChange={handleInputChange}
            disabled={isFieldDisabled}
            error={!eventFieldValidity.maxCapacity.input}
            helperText={
              !eventFieldValidity.maxCapacity.input
                ? validationMessageMap.get(
                    EventFormValidationMessageTypes.MaxCapacity
                  )
                : null
            }
          />
          <LocationTypeField
            label={t(
              'Events.EventInformationField.FormField.EventLocationType',
              'Event Location Type'
            )}
            inputProps={{
              'aria-label': t(
                'Events.EventInformationField.AriaLabel.EventLocationType',
                'Event Location Type'
              ),
            }}
            variant="filled"
            disabled={isFieldDisabled}
            select={!isFieldDisabled}
            value={eventLocationType}
            name="eventLocationType"
            onChange={handleInputChange}
            error={!eventFieldValidity.eventLocationType.input}
            helperText={
              !eventFieldValidity.eventLocationType.input
                ? validationMessageMap.get(
                    EventFormValidationMessageTypes.EventLocationType
                  )
                : null
            }
          >
            {eventLocationTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </LocationTypeField>
        </Box>
        {possibleNewOwners && possibleNewOwners.length > 0 && (
          <OwnerField
            label={t(
              'Events.EventInformationField.FormField.PossibleNew',
              'Event Owner'
            )}
            inputProps={{
              'aria-label': t(
                'Events.EventInformationField.AriaLabel.PossibleNew',
                'Event Owner'
              ),
            }}
            variant="filled"
            disabled={isFieldDisabled}
            select={!isFieldDisabled}
            SelectProps={{ onChange: onOwnerChange }}
            name="eventOwner"
            value={owner}
          >
            {alphabeticallySortedOwners.map((option) => (
              <MenuItem
                key={option.actorKey}
                value={`${option.user} / ${option.supervisorUser} / ${option.role}`}
              >
                {`${option.user} / ${option.supervisorUser} / ${option.role} `}
              </MenuItem>
            ))}
          </OwnerField>
        )}

        {category === EventCategory.LeadershipTraining && (
          <Box sx={{ margin: 1 }}>
            <EventRegistrationRolesSelector
              roles={leadershipTrainingRoles}
              selectedRoles={eventInformationDetails.leadershipTrainingRoles}
              onChange={onSelectedRolesChange}
              disabled={isFieldDisabled}
            />
            {!eventFieldValidity.roles.input && (
              <FormHelperText
                sx={{
                  color: theme.palette.error.main,
                }}
              >
                {t(
                  'Events.EventInformationField.RolesValidationMessage',
                  'Roles are required, please select at least one role.'
                )}
              </FormHelperText>
            )}
          </Box>
        )}
      </Box>
    </EventSection>
  )
}

export default EventInformationFields
